import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner, PageContainer, EmptyState } from './components/reusable';
import AppLayout from './components/layout/AppLayout';
import RequireAuth from './auth/RequireAuth';
import RequireRole from './auth/RequireRole';
import SponsorLayout from './components/layout/SponsorLayout';
import { useAuth } from './auth/AuthProvider';
import Login from './pages/Login/Login';
import { OIDC_ENABLED } from './auth/oidc/oidcConfig';
import OidcLogin from './auth/oidc/OidcLogin';
import AuthCallback from './auth/oidc/AuthCallback';
import { EMBEDDED_ENABLED } from './auth/embedded/embeddedConfig';
import EmbeddedLogin from './auth/embedded/EmbeddedLogin';
import Portfolio from './pages/Portfolio/Portfolio';
import Manage from './pages/Manage/Manage';
import KitchenSink from './pages/KitchenSink/KitchenSink';

// Top-level routes are lazy-loaded (Frontend Rules Rule 6); existing stubs stay
// eager until they grow real screens.
//
// The Dashboard is the heaviest screen (charts, feature widgets) — lazy-load it
// so it isn't in the initial bundle (FRONTEND_RULES §6/§11). Other routes can
// follow the same pattern as they grow.
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));

// The sponsor persona is a separate route tree with its own shell — lazy-loaded so a
// participant never downloads it (Frontend Rules Rule 6).
const SponsorHome = lazy(() => import('./pages/sponsor/SponsorHome/SponsorHome'));

// "/sponsor" index: every sponsor screen is plan-scoped, so land straight on a plan
// rather than on a picker (ADR #18, the sponsor counterpart of ADR #16). From the
// Relius portal that is ONLY the plan the handoff names (`session.planId`), never
// swapped for another one: a plan with no data shows the plan page's own error state.
// The first plan is only for sessions with no handoff plan (local dummy / OIDC logins).
// Switching plans is the avatar dropdown's job. The picker page
// (`pages/sponsor/SponsorLanding`) is kept unmounted; to restore it, lazy-import it and
// put it back on the "sponsor" route below.
// A sponsor with no plans is a provisioning problem, so say so instead of redirecting
// nowhere.
function SponsorIndexRoute() {
  const { t } = useTranslation();
  const { sponsorPlans = [], session } = useAuth();
  const planId = session?.planId ?? sponsorPlans[0]?.planId;
  if (!planId) {
    return (
      <PageContainer maxWidth={1440}>
        <EmptyState
          title={t('sponsor.landing.emptyTitle')}
          message={t('sponsor.landing.emptyMessage')}
        />
      </PageContainer>
    );
  }
  return <Navigate to={`/sponsor/${planId}`} replace />;
}

// Index ("/") gate: "/" holds no screen of its own — it just routes each persona to
// their home. The participant plan-picker that used to live here is gone; switching
// plans is the avatar dropdown's job (AccountMenu → ProfileMenu), and the account it
// opens on is already chosen for them (auth.slice fetchMe defaults selectedAccountId
// to the persisted account, else the FIRST one). By the time this renders, RequireAuth
// has confirmed the session, so `accounts` is loaded.
// Covers every entry to "/": post-login, refresh, manual nav, and the "*" fallback.
function HomeRoute() {
  const { isSponsor } = useAuth();
  // A sponsor holds no retirement account of their own, so the participant Dashboard has
  // nothing to show them — send them to the sponsor plan summary instead. It is also the
  // path the Relius portal lands a sponsor on (/sponsor, as /participant is theirs), so
  // the two entries agree.
  if (isSponsor) return <Navigate to="/sponsor" replace />;
  return <Navigate to="/dashboard" replace />;
}

// App routing. The shared shell (header/footer/container) is the layout
// route; each page renders into its <Outlet />. "/" is a redirect-only index
// (Dashboard for a participant, /sponsor for a sponsor); unknown paths — and
// the legacy /landing URL — fall back to it.
function App() {
  return (
    <Routes>
      {/* Full-screen login (no app shell). Public route. The auth mode picks the
          screen: OIDC = IdP sign-in + PKCE callback; embedded = the silent parent
          handshake; otherwise the dummy participant picker. Modes are mutually
          exclusive (REACT_APP_AUTH_MODE). */}
      <Route
        path="login"
        element={
          // eslint-disable-next-line no-nested-ternary
          OIDC_ENABLED ? <OidcLogin /> : (EMBEDDED_ENABLED ? <EmbeddedLogin /> : <Login />)
        }
      />
      {OIDC_ENABLED && <Route path="auth/callback" element={<AuthCallback />} />}

      {/* The authenticated app shell — everything below requires a session. */}
      <Route
        element={(
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        )}
      >
        <Route index element={<HomeRoute />} />
        <Route path="landing" element={<Navigate to="/" replace />} />
        {/* The Relius portal lands a participant here (its sponsor counterpart is
            /sponsor). A named route rather than the "*" fallback below, so the parent's
            contract is explicit and survives any change to that fallback. */}
        <Route path="participant" element={<Navigate to="/dashboard" replace />} />
        <Route path='dashboard' element={<Dashboard />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="manage" element={<Manage />} />
        {/* Internal preview/QA harness for the reusable library (Phase 1). */}
        <Route path="kitchen-sink" element={<KitchenSink />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* The sponsor persona: its own layout route, its own shell, gated on the role
          from /me. A participant landing here is redirected to their own home; the API
          independently answers 403, so this is the UX half of the gate, not the
          security boundary. */}
      <Route
        element={(
          <RequireAuth>
            <RequireRole role="sponsor" redirectTo="/">
              <SponsorLayout />
            </RequireRole>
          </RequireAuth>
        )}
      >
        <Route path="sponsor" element={<SponsorIndexRoute />} />
        <Route
          path="sponsor/:planId"
          element={(
            <Suspense fallback={<Spinner size="lg" label="Loading plan" />}>
              <SponsorHome />
            </Suspense>
          )}
        />
      </Route>
    </Routes>
  );
}

export default App;
